import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Trans } from 'react-i18next';

import { FormattedProps, FormattedRenewalOfferSummaryData } from '../types';
import {
  AlertBoxContainer,
  Buttons,
  Card,
  Container,
  Option,
  OptionLabel,
  OptionsContainer,
  OptionsList,
  RenewalDateLabel,
  Tax,
  TaxLabel,
  Title,
  Total
} from '../styles/components';
import { useRenewalOfferSummary } from '../hooks/useRenewalOfferSummary';
import Loader from 'shared/blocks/selfcare/shared/components/Loader';
import OfferDetailsRocContainer from '../../../shared/containers/OfferDetailsRocContainer';
import AlertBox from 'shared/components/presentational/AlertBox/AlertBox';
import { Button } from '../../../shared/components/Button/Button';
import { formatRenewalOfferSummaryData } from '../helpers/formatRenewalOfferSummaryData';
import { RenewalFormState, ADD_AMOUNT_AND_CURRENCY } from '../../reducer';

function Summary({
  title,
  productPriceLabel,
  renewalDateLabel,
  majorProduct,
  minorProductsTitle,
  minorProductsEngagementUnit,
  totalLabel,
  submitButton,
  cancelButtonLabel,
  apiPostRenewalOfferSummary
}: FormattedProps) {
  const location = useLocation();
  const history = useHistory();
  const [
    summaryData,
    setSummaryData
  ] = useState<FormattedRenewalOfferSummaryData | null>(null);

  const renewalForm = useSelector(
    (state: { renewalForm: RenewalFormState }) => state.renewalForm
  );
  const dispatch = useDispatch();

  const { isIdle, isError, isLoading, mutate } = useRenewalOfferSummary({
    apiPostRenewalOfferSummary,
    saleDeviceId: renewalForm.saleDeviceId
  });

  useEffect(() => {
    if (renewalForm && !summaryData) {
      const queryParams = new URLSearchParams(location.search);
      const majorProductId = queryParams.get('product');
      const minorProductsParam = queryParams.get('minorProducts');

      const productsArray = minorProductsParam
        ? [majorProductId, ...minorProductsParam.split(',')]
        : [majorProductId];

      mutate(
        {
          token: renewalForm.token,
          correlationId: renewalForm.correlationId,
          managementAct: renewalForm.managementAct,
          body: {
            equipmentId: renewalForm.equipmentId,
            paymentMeanId: renewalForm.paymentMeanId,
            personId: renewalForm.personId,
            contractId: renewalForm.contractId,
            data: { products: productsArray.map(id => ({ id })) }
          }
        },
        {
          onSuccess: data => {
            setSummaryData(
              formatRenewalOfferSummaryData(
                data,
                majorProduct,
                minorProductsEngagementUnit
              )
            );
          }
        }
      );
    }
  }, [
    renewalForm,
    location.search,
    mutate,
    majorProduct,
    minorProductsEngagementUnit,
    summaryData
  ]);

  if (isLoading || isIdle) {
    return <Loader />;
  }

  if (isError || !summaryData) {
    return (
      <AlertBoxContainer>
        <AlertBox
          status="error"
          content={<Trans i18nKey="blocks.renewal.qrCodeScanError" />}
        />
      </AlertBoxContainer>
    );
  }

  const handleSubmit = () => {
    dispatch({
      type: ADD_AMOUNT_AND_CURRENCY,
      payload: {
        amount: summaryData.amount,
        currency: summaryData.currency
      }
    });
    history.push(submitButton.link);
  };

  const handleCancelButton = () => {
    history.goBack();
  };

  return (
    <Container>
      {title && <Title htmlTag={title.htmlTag}>{title.label}</Title>}

      <div>
        {summaryData.majorProduct && (
          <Card>
            <Title htmlTag="h2">{summaryData.majorProduct.title}</Title>
            <OfferDetailsRocContainer
              price={summaryData.majorProduct.price}
              priceInformation={productPriceLabel ?? ''}
              engagementUnit={summaryData.majorProduct.engagementUnit}
              currency={summaryData.currency}
            />
            <RenewalDateLabel>
              {renewalDateLabel}&nbsp;{summaryData.contractStartDate}
            </RenewalDateLabel>
          </Card>
        )}
        {summaryData.tax.label && (
          <Tax>
            <TaxLabel>{summaryData.tax.label}&nbsp;</TaxLabel>
            <OfferDetailsRocContainer
              price={summaryData.tax.amount}
              currency={summaryData.currency}
              variant="text"
            />
          </Tax>
        )}
      </div>

      {summaryData.minorProducts.length > 0 && (
        <OptionsContainer>
          <>
            {minorProductsTitle && (
              <Title htmlTag={minorProductsTitle.htmlTag}>
                {minorProductsTitle.label}
              </Title>
            )}
            <OptionsList>
              {summaryData.minorProducts.map(minorProduct => (
                <Option key={minorProduct.id}>
                  <OptionLabel>{minorProduct.title}</OptionLabel>
                  <OfferDetailsRocContainer
                    price={minorProduct.price}
                    engagementUnit={minorProduct.engagementUnit}
                    variant="modal"
                    currency={summaryData.currency}
                  />
                </Option>
              ))}
            </OptionsList>
          </>
        </OptionsContainer>
      )}

      <Total>
        <Title htmlTag="h3">{totalLabel}</Title>
        <OfferDetailsRocContainer
          price={summaryData.amount}
          variant="modal"
          currency={summaryData.currency}
        />
      </Total>

      <Buttons>
        <Button
          label={submitButton.label}
          variant="primary"
          fullWidthMobile
          fullWidthDesktop
          onClick={handleSubmit}
        />
        <Button
          label={cancelButtonLabel}
          variant="secondary"
          fullWidthMobile
          fullWidthDesktop
          onClick={handleCancelButton}
        />
      </Buttons>
    </Container>
  );
}

export default Summary;
